import React, { useEffect, useState, useRef } from 'react'
import { UpdatedRustDemo, UpdatedJavaScriptDemo } from './UpdatedExamples'
import MobileShareSlideshow from './MobileShareSlideshow'

interface DemoViewerProps {
  demoId: string | null
}

interface DemoData {
  sourceLanguage: string
  targetLanguage: string
  html: string
}

/**
 * DemoViewer - Renders static demos in immersive slideshow format
 * Extracts HTML from demo components and passes to MobileShareSlideshow
 */
const DemoViewer: React.FC<DemoViewerProps> = ({ demoId }) => {
  const [demoData, setDemoData] = useState<DemoData | null>(null)
  const rustRef = useRef<HTMLDivElement>(null)
  const jsRef = useRef<HTMLDivElement>(null)

  // Extract HTML from demo components on mount
  useEffect(() => {
    if (!demoId) return

    // Wait for refs to be populated
    const timer = setTimeout(() => {
      let html = ''
      let sourceLanguage = ''
      let targetLanguage = ''

      if (demoId === 'rust' && rustRef.current) {
        html = rustRef.current.innerHTML
        sourceLanguage = 'C'
        targetLanguage = 'Rust'
      } else if (demoId === 'javascript' && jsRef.current) {
        html = jsRef.current.innerHTML
        sourceLanguage = 'Python'
        targetLanguage = 'JavaScript'
      }

      if (html) {
        setDemoData({ sourceLanguage, targetLanguage, html })
      }
    }, 100) // Small delay to ensure DOM is ready

    return () => clearTimeout(timer)
  }, [demoId])

  // Invalid demo ID
  if (!demoId || (demoId !== 'rust' && demoId !== 'javascript')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-2">Demo Not Found</h1>
          <p className="text-gray-400 mb-4">Invalid demo ID</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    )
  }

  // Loading state while extracting HTML
  if (!demoData) {
    return (
      <>
        {/* Hidden demo components to extract HTML */}
        <div className="hidden">
          {demoId === 'rust' && (
            <div ref={rustRef}>
              <UpdatedRustDemo />
            </div>
          )}
          {demoId === 'javascript' && (
            <div ref={jsRef}>
              <UpdatedJavaScriptDemo />
            </div>
          )}
        </div>

        {/* Loading spinner */}
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full mx-auto mb-4"></div>
            <p className="text-lg">Loading demo...</p>
          </div>
        </div>
      </>
    )
  }

  // Render slideshow with demo data
  return (
    <MobileShareSlideshow
      sourceLanguage={demoData.sourceLanguage}
      targetLanguage={demoData.targetLanguage}
      htmlContent={demoData.html}
    />
  )
}

export default DemoViewer
